import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { watchLivePage } from './livePageStream';
import { elementAtPoint } from './liveGuidance';
import '@rrweb/replay/dist/style.css';

export default function LivePageViewer({ sessionId }) {
  const { t } = useTranslation('supportChat');
  const viewer = useRef(null);
  const stage = useRef(null);
  const root = useRef(null);
  const [status, setStatus] = useState('loading');
  const [ready, setReady] = useState(false);
  const [fit, setFit] = useState(true);
  const [pointing, setPointing] = useState(false);
  const [pointStatus, setPointStatus] = useState('');
  const [metrics, setMetrics] = useState(null);
  const currentPlayer = useRef(null);
  const connection = useRef(null);
  const fitMode = useRef(fit);
  const allowCanvasReplay = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const requestResize = useRef(null);
  fitMode.current = fit;
  useEffect(() => {
    let disposed = false, replayer, Player, appliedResolve, appliedReject, appliedTimer, lastEvent;
    let hasSnapshot = false;
    const library = import('@rrweb/replay');
    let clockOffset = null;
    let dimensions = { width: 0, height: 0 };
    let eventsBeforeReady = [];
    let resizeFrame = null;
    let observedWidth = null;
    let appliedSize = '';
    const resize = () => {
      resizeFrame = null;
      if (disposed || !replayer || !stage.current || !root.current) return;
      const width = Number(replayer.iframe.width) || dimensions.width;
      const height = Number(replayer.iframe.height) || dimensions.height;
      const availableWidth = Math.floor(stage.current.clientWidth);
      if (width <= 0 || height <= 0 || availableWidth <= 0) return;
      const scale = fitMode.current ? Math.min(1, availableWidth / width) : 1;
      const size = `${width}:${height}:${scale}`;
      if (size === appliedSize) return;
      appliedSize = size;
      replayer.wrapper.style.transformOrigin = 'top left';
      replayer.wrapper.style.transform = `scale(${scale})`;
      root.current.style.width = `${Math.floor(width * scale)}px`;
      root.current.style.height = `${Math.ceil(height * scale)}px`;
    };
    // ResizeObserver must only measure. Writing descendant sizes during its
    // notification changes the observed auto-height and triggers another delivery.
    const scheduleResize = () => {
      if (!disposed && resizeFrame === null) resizeFrame = requestAnimationFrame(resize);
    };
    requestResize.current = scheduleResize;
    const observer = new ResizeObserver(entries => {
      const entry = entries.find(item => item.target === stage.current);
      if (!entry || entry.contentRect.width === observedWidth) return;
      observedWidth = entry.contentRect.width;
      scheduleResize();
    });
    observer.observe(stage.current);
    const createPlayer = () => {
      replayer?.destroy();
      replayer = new Player([], { root: root.current, liveMode: true, mouseTail: false, showWarning: false,
        pauseAnimation: false, triggerFocus: false, UNSAFE_replayCanvas: allowCanvasReplay,
        insertStyleRules: ['.eo-observation, .eo-support-pointer { display: none !important; }'] });
      currentPlayer.current = replayer;
      clockOffset = null; appliedSize = '';
      replayer.startLive(Date.now() - 50);
      replayer.on('resize', scheduleResize);
      replayer.on('event-cast', event => {
        if (event === lastEvent || (event?.timestamp === lastEvent?.timestamp && event?.type === lastEvent?.type)) {
          clearTimeout(appliedTimer); appliedResolve?.(); appliedResolve = null; appliedReject = null;
        }
      });
    };
    const add = events => {
      // Align remote timestamps to the receiver clock; keep the relative timing.
      if (clockOffset === null && events.length) clockOffset = Date.now() - events[0].timestamp;
      for (const event of events) {
        if (event.type === 4) dimensions = { width: event.data.width, height: event.data.height };
        const aligned = { ...event, timestamp: event.timestamp + clockOffset };
        if (event === events[events.length - 1]) lastEvent = aligned;
        replayer.addEvent(aligned);
      }
      if (events.some(event => event.type === 2)) replayer.wrapper.style.visibility = 'visible';
      if (events.some(event => event.type === 2 || event.type === 4)) scheduleResize();
    };
    const stop = watchLivePage({ sessionId,
      onMetrics: value => { if (!disposed) setMetrics(value); },
      onStatus: next => {
        if (disposed) return;
        setStatus(next);
        if (['ended', 'disconnected', 'waiting'].includes(next)) {
          setReady(false);
          if (replayer) replayer.wrapper.style.visibility = 'hidden';
        }
      },
      onEvents: (events, packet) => {
        if (packet) return library.then(({ Replayer }) => {
          if (disposed) return;
          Player = Replayer;
          if (!replayer || (packet.full && hasSnapshot)) createPlayer();
          if (packet.full) hasSnapshot = true;
          const applied = new Promise((resolve, reject) => {
            appliedResolve = resolve; appliedReject = reject;
            appliedTimer = setTimeout(() => { appliedResolve = null; appliedReject = null; reject(new Error('Replay atrasado')); }, 1500);
          });
          add(events); setStatus('connected');
          if (packet.full) setReady(true);
          return applied;
        });
        if (disposed) return;
        if (events.length) setStatus('connected');
        if (!replayer) {
          if (eventsBeforeReady.length + events.length > 500) { eventsBeforeReady = []; stop.resync?.(); return; }
          eventsBeforeReady.push(...events); return;
        }
        add(events);
        if (events.some(event => event.type === 2)) setReady(true);
      },
    });
    connection.current = stop;
    library.then(({ Replayer }) => {
      if (disposed) return;
      Player = Replayer;
      if (!replayer) createPlayer();
      if (eventsBeforeReady.length) {
        add(eventsBeforeReady);
        if (eventsBeforeReady.some(event => event.type === 2)) setReady(true);
        eventsBeforeReady = [];
      }
    }).catch(() => { if (!disposed) setStatus('error'); });
    return () => {
      disposed = true;
      stop(); observer.disconnect();
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
      if (requestResize.current === scheduleResize) requestResize.current = null;
      clearTimeout(appliedTimer); appliedReject?.(new Error('Sessão encerrada'));
      currentPlayer.current = null; connection.current = null;
      replayer?.destroy();
    };
  }, [sessionId, allowCanvasReplay]);
  useEffect(() => { requestResize.current?.(); }, [fit]);
  const point = async event => {
    if (!pointing || !ready) return;
    event.preventDefault(); event.stopPropagation();
    const id = elementAtPoint(currentPlayer.current, event.clientX, event.clientY);
    if (!id) { setPointStatus(t('liveGuidance.selectVisibleElement')); return; }
    const ok = await connection.current?.point?.(id);
    setPointStatus(ok ? t('liveGuidance.indicationShown') : t('liveGuidance.pageChanged'));
  };
  const full = () => viewer.current?.requestFullscreen?.();
  return <div ref={viewer} className="eo-live-page-viewer">
    <div className="eo-live-view-toolbar"><span>{status === 'connected' ? t('livePageViewer.connectionLive') : status === 'ended' ? t('livePageViewer.sessionEnded') : status === 'disconnected' ? t('livePageViewer.reconnecting') : t('livePageViewer.waitingForUser')}</span>
      <button type="button" disabled={!ready} aria-pressed={pointing} onClick={() => { setPointing(value => !value); setPointStatus(''); }}>{t('livePageViewer.pointElement')}</button>
      <button type="button" onClick={() => setFit(value => !value)}>{fit ? t('livePageViewer.originalSize') : t('livePageViewer.fitWidth')}</button><button type="button" onClick={full}>{t('livePageViewer.fullscreen')}</button></div>
    {pointing && <p role="status">{t('livePageViewer.pointingHint')}</p>}
    {pointStatus && <p role="status">{pointStatus}</p>}
    {metrics && <small>{t('livePageViewer.firstSnapshot', { ms: metrics.firstFrame })}{metrics.latencyP95 !== null ? t('livePageViewer.estimatedDelay', { ms: metrics.latencyP95 }) : ''}{t('livePageViewer.applyMs', { ms: metrics.applyMs })}{t('livePageViewer.kbps', { kbps: metrics.kbps })}{metrics.heapMB ? t('livePageViewer.memoryTab', { mb: metrics.heapMB }) : ''}</small>}
    {!ready && <p role="status">{status === 'error' ? t('livePageViewer.loadErrorViewer') : t('livePageViewer.loadingViewer')}</p>}
    <div ref={stage} className={`eo-live-page-stage${pointing ? ' eo-pointing' : ''}`} onClick={point} role="region" aria-label={t('livePageViewer.stageAria')}><div ref={root} className="eo-live-page-root" /></div>
  </div>;
}
